import { HttpLink, type HttpOptions } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import {
  type AuthStrategy,
  type MockedAuthStrategyOptions,
  createCandidateAuthStrategy,
} from '@seek/online-identity';
import type { AuthStrategyType } from '@seek/online-identity/lib-types/modules/auth-strategies/types';
import fetch from 'unfetch';

import type { Metrics } from '../types';

import {
  type Brands,
  type CountryCode,
  type SeekHeaders,
  getSeekHeaders,
} from './getSeekHeaders';
import getTrackingHeaders, { type TrackingHeaders } from './getTrackingHeaders';
import { formatMetrics } from './metrics';

if (typeof window !== 'undefined' && !window.fetch) {
  window.fetch = fetch;
}
interface TokenData {
  authenticated?: boolean;
  token?: string;
  tokenHeader?: string;
}

type Fetch = NonNullable<HttpOptions['fetch']>;

const browserFetch =
  (
    authStrategy: AuthStrategy | null,
    appName: string,
    trackingHeaders: TrackingHeaders,
    seekHeaders: SeekHeaders,
    headers: Record<string, any>,
    metricsHandler?: (metrics: Metrics) => void,
  ): Fetch =>
  (url, options) => {
    const fetchWithOptions = (tokenData: TokenData) => {
      const authHeaders: TrackingHeaders = {};

      if (tokenData.authenticated) {
        const { tokenHeader = '', token = '' } = tokenData;
        authHeaders[tokenHeader] = token;
      }

      const modifiedOptions = {
        ...options,
        headers: {
          ...headers,
          ...options?.headers,
          ...trackingHeaders,
          ...seekHeaders,
          ...authHeaders,
          'X-Seek-Site': appName,
        },
      };

      const startTime = Date.now();

      return fetch(url, modifiedOptions).then((response) => {
        metricsHandler?.(formatMetrics(startTime, response));
        return response;
      });
    };

    return authStrategy
      ? authStrategy
          .get()
          .catch(
            () =>
              // swallow auth api errors
              ({ authenticated: false } as const),
          )
          .then(fetchWithOptions)
      : fetchWithOptions({});
  };

export type AuthOptions =
  | {
      useAuth: true;
      useMockedAuth?: boolean;
      hostOverride?: string;
      mockOptions?: MockedAuthStrategyOptions;
      forceStrategy?: AuthStrategyType;
    }
  | {
      useAuth: false;
    };

interface HttpClientOptions {
  appName: string;
  graphqlEndpointOverride?: string;
  headers?: Record<string, any>;
  batchRequests?: boolean;
  batchRequestOptions?: BatchHttpLink.Options;
  authOptions: AuthOptions;
  brand?: Brands;
  country?: CountryCode;
  metricsHandler?: (metrics: Metrics) => void;
}

export const browserHttpLink = ({
  appName,
  headers,
  graphqlEndpointOverride,
  batchRequests = false,
  batchRequestOptions = {},
  authOptions,
  brand,
  country,
  metricsHandler,
}: HttpClientOptions) => {
  const trackingHeaders = getTrackingHeaders();
  const hostFallback =
    typeof window !== 'undefined' ? window.location?.host : '';
  const seekHeaders = getSeekHeaders({
    host: authOptions.useAuth
      ? authOptions.hostOverride || hostFallback
      : hostFallback,
    brand,
    country,
  });

  let authStrategy = null;
  if (authOptions.useAuth) {
    authStrategy = createCandidateAuthStrategy({
      hostOverride: authOptions.hostOverride,
      mockOptions: authOptions.mockOptions,
      useMockedAuth: authOptions.useMockedAuth,
      forceStrategy: authOptions.forceStrategy,
    });
  }

  const config = {
    uri: graphqlEndpointOverride ?? '/graphql',
    fetch: browserFetch(
      authStrategy,
      appName,
      trackingHeaders,
      seekHeaders,
      headers || {},
      metricsHandler,
    ),
  };

  return batchRequests
    ? new BatchHttpLink({ ...config, ...batchRequestOptions })
    : new HttpLink(config);
};

export { browserFetch as browserFetchForTest };
